import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IIntegrationUIToggleSection } from '@feathr/components';
import { ConfirmModal, SyncRuleSection } from '@feathr/components';

import { BLACKBAUD_ICON } from '../../blackbaudIcon';
import {
  disableToggle,
  getActivityValues,
  stageUpsyncActivities,
  stageUpsyncActivity,
} from '../../RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';
import { FORM_SUBMISSION_UPSYNC_KEY, PERSON_UPSYNC_KEY } from '../../raisersEdgeConfig';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** The config's create-constituents section: the person upsync toggle and its sources. */
  section: IIntegrationUIToggleSection;
  /** Disables every toggle with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

/**
 * Person upsync: the toggle that gates creating Blackbaud constituents, and its sources. Turning
 * the gate on or off asks for confirmation first. Edits are staged for the page's Save & Sync.
 */
function RaisersEdgePersonUpsync({
  disabledReason,
  integration,
  section,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // Kept after close, so the modal's copy doesn't flip while it animates out.
  const [confirmValue, setConfirmValue] = useState(false);
  const values = getActivityValues({ upsync_activities: integration.get('upsync_activities') });
  const isAvailable = integration.get('person_upsync_available') === true;
  const unavailableReason = isAvailable
    ? undefined
    : t('Person upsync isn’t available for this Raiser’s Edge connection yet.');
  const shownSection =
    !isAvailable || values[PERSON_UPSYNC_KEY]
      ? section
      : disableToggle(
          [section],
          FORM_SUBMISSION_UPSYNC_KEY,
          t('Turn on Feathr to Raiser’s Edge upsync to use this source.'),
        )[0];

  function handleChange(key: string, value: boolean): void {
    if (key === PERSON_UPSYNC_KEY) {
      setConfirmValue(value);
      setIsConfirmOpen(true);
    } else if (key === FORM_SUBMISSION_UPSYNC_KEY) {
      stageUpsyncActivity(integration, 'people_from_form_submissions', value);
    }
  }

  function handleClose(): void {
    setIsConfirmOpen(false);
  }

  function handleConfirm(): void {
    // The gate going off clears its sources, so turning it back on can't silently resume one.
    stageUpsyncActivities(
      integration,
      confirmValue ? { people: true } : { people: false, people_from_form_submissions: false },
    );
    setIsConfirmOpen(false);
  }

  return (
    <>
      <SyncRuleSection
        crmIcon={BLACKBAUD_ICON}
        disabledReason={disabledReason ?? unavailableReason}
        name={`raisers-edge-${section.key}`}
        onChange={handleChange}
        section={shownSection}
        values={values}
      />
      <ConfirmModal
        confirmButtonText={confirmValue ? t('Turn on') : t('Turn off')}
        confirmButtonType={confirmValue ? 'primary' : 'danger'}
        description={
          confirmValue
            ? t(
                'Feathr will create a new Blackbaud constituent for each new person from the sources you turn on, if they don’t already exist. Existing constituents are never updated. Save & Sync to apply it.',
              )
            : t(
                'Feathr will stop creating Blackbaud constituents from every source. Constituents already created stay in Blackbaud. Save & Sync to apply it.',
              )
        }
        onClose={handleClose}
        onConfirm={handleConfirm}
        opened={isConfirmOpen}
        t={t}
        title={
          confirmValue
            ? t('Turn on Feathr to Raiser’s Edge upsync?')
            : t('Turn off Feathr to Raiser’s Edge upsync?')
        }
      />
    </>
  );
}

export default observer(RaisersEdgePersonUpsync);
